import React, { useState, useEffect, useRef } from "react";
import { useTheme, alpha } from "@mui/material/styles";
import {
  Box,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Menu,
  IconButton,
  Button,
  Typography,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  useMediaQuery,
} from "@mui/material";

import { Add } from "@mui/icons-material";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";

import {
  getEscandalloSubgruposMuestras,
  getProveedores,
} from "../../api/Muestras/muestraApi";

import debounce from "lodash/debounce";

import { getExchangeRate } from "../../api/Currency/exchangeRateApi";

import {
  createBudgetLine,
  updateBudgetLine,
  fetchBudgetLines,
  deleteBudgetLine,
} from "../../api/Estimates/budgetLinesApi";
import {
  applyBudgetLinePricing,
  computeCosteConvertido,
  isBudgetLineComplete,
  normalizeDecimalTyping,
  PERCENT_IMPORT_SUBGRUPOS,
  shouldSkipAutoSubgroupCostCalc,
} from "../../utils/estimatesLineasUtils";
import {
  computeDutyAmount,
  computeImportRecalcBases,
  parseLooseDecimal,
  resolveEffectiveDutyPct,
  safeDecimal,
} from "../../utils/estimatesImportUtils";
import { getDdpImportTarifas } from "../../api/Estimates/ddpImportTarifasApi";
import { pageMobileCardSx, pageSectionTitleSx } from "../../styles/pageModernStyles";

/** Fuera del componente: si se define dentro, React lo trata como tipo nuevo en cada render y los inputs pierden el foco. */
const FieldPair = ({ isMobile, children }) =>
  isMobile ? (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "1fr 1fr",
        gap: 0.75,
        gridColumn: "1 / -1",
        width: "100%",
      }}
    >
      {children}
    </Box>
  ) : (
    children
  );

const newRowKey = () =>
  `row-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 9)}`;

const NUMERIC_DRAFT_FIELDS = [
  "Coste",
  "Un",
  "MargenVenta",
  "MargenCoste",
  "Venta",
];

const withRowKey = (line) =>
  line?._rowKey
    ? line
    : {
        ...line,
        _rowKey:
          line?.IdLineaPresupuestos != null
            ? `id-${line.IdLineaPresupuestos}`
            : newRowKey(),
      };

const EstimatesLineas = ({
  presupuesto,
  presupuestoId,
  escandalloGrupos = [],
  escandalloSubgrupos = [],
  materialSubgrupos = [],
  onSave,
  initialLines = [],
  isLocked,
  defaultSupplier = null,
  refreshKey,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [lines, setLines] = useState([]);
  const [tiposProveedores, setTiposProveedores] = useState([]);
  const [ddpTarifas, setDdpTarifas] = useState([]);
  const currencyList = ["USD", "EUR", "CNY", "TRY"];

  const [confirmDelete, setConfirmDelete] = useState(null);
  const [currencyMenu, setCurrencyMenu] = useState({
    anchorEl: null,
    index: null,
    field: null,
  });
  const ddpRecalcDoneRef = useRef(false);
  /** Evita que un calculateLine async antiguo pise decimales a medio teclear ("28."). */
  const lineCalcSeqRef = useRef(0);
  const linesRef = useRef(lines);
  linesRef.current = lines;

  // Emitir líneas hacia el padre
  useEffect(() => {
    if (onSave) onSave(lines);
  }, [lines, onSave]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tarifas = await getDdpImportTarifas();
        if (!cancelled) setDdpTarifas(tarifas);
      } catch (error) {
        console.warn("No se pudieron cargar tarifas DDP:", error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleAddLine = async () => {
    const defaultGroup = 14;
    const defaultSubgroup = 200;
    let subgruposList = [];
    try {
      subgruposList = await getEscandalloSubgruposMuestras(defaultGroup);
    } catch (error) {
      console.error("Error al cargar subgrupos por defecto:", error);
    }

    setLines((prev) => [
      ...prev,
      {
        _rowKey: newRowKey(),
        ESCANDALLO_GRUPOS: defaultGroup,
        ESCANDALLO_SUBGRUPO: defaultSubgroup,
        PROVEEDOR_ESCANDALLO: defaultSupplier || "",
        Un: 1,
        Coste: 0,
        Venta: 0,
        MonedaCoste: "USD",
        MonedaVenta: "USD",
        TipoCambio: 0,
        CosteConvertido: 0,
        subgruposList,
      },
    ]);
  };

  const handleRemoveLine = (index) => setConfirmDelete(index);

  const confirmRemoveLine = async () => {
    if (confirmDelete === null) return;
    const lineToRemove = lines[confirmDelete];

    if (lineToRemove.IdLineaPresupuestos) {
      try {
        await deleteBudgetLine(lineToRemove.IdLineaPresupuestos);
      } catch (error) {
        console.error("Error al eliminar la línea:", error);
        alert("Error al eliminar la línea.");
        return;
      }
    }

    setLines((prev) => prev.filter((_, i) => i !== confirmDelete));
    setConfirmDelete(null);
  };

  const isLineComplete = isBudgetLineComplete;

  const compactFieldHeight = 38;

  const softFieldSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: 2.4,
      height: compactFieldHeight,
      minHeight: compactFieldHeight,
      alignItems: "center",
      backgroundColor:
        theme.palette.mode === "dark"
          ? theme.palette.background.cajaInpunts
          : alpha(theme.palette.common.white, 0.98),
      boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.05)}`,
    },
    "& .MuiInputBase-input": {
      height: "100%",
      boxSizing: "border-box",
      paddingTop: 0,
      paddingBottom: 0,
      display: "flex",
      alignItems: "center",
      fontSize: "0.84rem",
    },
    "& .MuiSelect-select": {
      height: "100% !important",
      minHeight: "unset !important",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      paddingTop: "0 !important",
      paddingBottom: "0 !important",
    },
  };
  const lineGridTemplateColumns =
    "minmax(0, 1.65fr) minmax(0, 1.5fr) minmax(0, 1.95fr) minmax(3.6rem, 0.7fr) minmax(3.3rem, 0.6fr) minmax(3.8rem, 0.72fr) minmax(3rem, 0.55fr) minmax(4rem, 0.78fr) minmax(3.3rem, 0.6fr) minmax(4rem, 0.78fr) minmax(4rem, 0.82fr) minmax(4rem, 0.82fr) 2rem";

  const labeledField = (label, node) => (
    <Box sx={{ minWidth: 0 }}>
      {isMobile && label ? (
        <Typography
          sx={{
            ...pageSectionTitleSx,
            fontSize: "0.62rem",
            mb: 0.35,
            display: "block",
          }}
        >
          {label}
        </Typography>
      ) : null}
      {node}
    </Box>
  );

  const currencyMetaByCode = {
    USD: { icon: "$", color: "#0ea5e9", bg: "rgba(14, 165, 233, 0.12)" },
    EUR: { icon: "EUR", color: "#00a58a", bg: "rgba(0, 194, 163, 0.12)" },
    CNY: { icon: "Y", color: "#ef4444", bg: "rgba(239, 68, 68, 0.12)" },
    TRY: { icon: "TL", color: "#f59e0b", bg: "rgba(245, 158, 11, 0.12)" },
  };

  const renderCurrencyPill = (code) => {
    const meta = currencyMetaByCode[code] || {
      icon: String(code || "?").slice(0, 3),
      color: theme.palette.text.primary,
      bg: alpha(theme.palette.text.primary, 0.08),
    };
    return (
      <Box
        component="span"
        sx={{
          minWidth: 34,
          height: 24,
          px: 0.8,
          borderRadius: 999,
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.72rem",
          fontWeight: 900,
          letterSpacing: 0.2,
          color: meta.color,
          bgcolor: meta.bg,
        }}
      >
        {meta.icon}
      </Box>
    );
  };

  const openCurrencyMenu = (event, index, field) => {
    if (isLocked) return;
    setCurrencyMenu({
      anchorEl: event.currentTarget,
      index,
      field,
    });
  };

  const closeCurrencyMenu = () => {
    setCurrencyMenu({
      anchorEl: null,
      index: null,
      field: null,
    });
  };

  const calculateLine = async (
    line,
    forceRecalc = false,
    dividirPorCien = false,
    triggerField = null,
  ) => {
    const updatedLine = { ...line };
    updatedLine.MargenVenta = updatedLine.MargenVenta ?? 10;

    const skipCostCalc = shouldSkipAutoSubgroupCostCalc(updatedLine, forceRecalc);

    if (
      updatedLine.MonedaCoste &&
      updatedLine.MonedaVenta &&
      updatedLine.Coste > 0 &&
      updatedLine.Un > 0 &&
      !skipCostCalc
    ) {
      try {
        const exchangeRate = await getExchangeRate(
          updatedLine.MonedaCoste,
          updatedLine.MonedaVenta,
        );
        if (!exchangeRate) return updatedLine;

        updatedLine.TipoCambio = parseFloat(exchangeRate);
        updatedLine.CosteConvertido = computeCosteConvertido({
          coste: updatedLine.Coste,
          un: updatedLine.Un,
          tipoCambio: updatedLine.TipoCambio,
          dividirPorCien,
        });
      } catch (error) {
        console.error("Error calculating line:", error);
      }
    }

    updatedLine.CosteConvertido = parseFloat(
      (updatedLine.CosteConvertido || 0).toFixed(2),
    );

    if (updatedLine.CosteConvertido > 0) {
      return applyBudgetLinePricing(updatedLine, triggerField);
    }

    return updatedLine;
  };

  // ===== Recalculo coherente de líneas DDP (import % y per CBM por UNIDAD) =====
  // ===== Recalculo coherente de líneas DDP (import % y per CBM por UNIDAD) =====
  const recalculateImportAutoLines = async (allLines) => {
    const {
      volPerUnit,
      insuranceBase,
      insuranceUn,
      cautionUn,
      lclCostCat,
      handlingCostCat,
      deliveryCostCat,
      refreshedSubtotalBaseImport,
      dutyPct,
    } = computeImportRecalcBases({
      presupuesto,
      allLines,
      tiposProveedores,
      escandalloSubgrupos,
      materialSubgrupos,
      defaultSupplier,
      tarifas: ddpTarifas,
    });
    const effectiveDutyPct = resolveEffectiveDutyPct(dutyPct, allLines);

    // Helper para actualizar (y persistir) una línea de importación
    const updateLine = async (
      subgrupoId,
      newUn,
      maybeNewCoste,
      newConvCost,
    ) => {
      const line = allLines.find(
        (l) =>
          Number(l.ESCANDALLO_GRUPOS) === 13 &&
          Number(l.ESCANDALLO_SUBGRUPO) === Number(subgrupoId),
      );
      if (!line) return;

      const updated = { ...line };

      // Un (si tenemos VOL válido, lo aplicamos; si no, dejamos el que había)
      updated.Un =
        Number.isFinite(newUn) && newUn > 0 ? safeDecimal(newUn, 6) : line.Un;

      // Coste: solo lo pisamos si tenemos uno válido (>0). Si no, conservamos el actual.
      const newCostNum = parseLooseDecimal(maybeNewCoste);
      if (newCostNum > 0) {
        updated.Coste = safeDecimal(newCostNum, 2);
      }

      updated.TipoCambio = 1;
      updated.MonedaCoste = "EUR";
      updated.MonedaVenta = "EUR";

      if (newConvCost != null) {
        updated.CosteConvertido = safeDecimal(newConvCost);
      } else if (PERCENT_IMPORT_SUBGRUPOS.has(Number(subgrupoId))) {
        updated.CosteConvertido = safeDecimal(
          (parseLooseDecimal(updated.Coste) * parseLooseDecimal(updated.Un)) / 100,
          2,
        );
      } else if (parseLooseDecimal(updated.Coste) > 0 && parseLooseDecimal(updated.Un) > 0) {
        updated.CosteConvertido = safeDecimal(
          parseLooseDecimal(updated.Coste) * parseLooseDecimal(updated.Un),
          2,
        );
      }

      // Recalcula Venta con su margen
      const margen = parseFloat(updated.MargenVenta ?? line.MargenVenta ?? 10);
      const denominator = 1 - (margen || 0) / 100;
      const isPercentLine = PERCENT_IMPORT_SUBGRUPOS.has(Number(subgrupoId));
      const conv =
        updated.CosteConvertido ??
        (isPercentLine
          ? safeDecimal(
              (parseLooseDecimal(updated.Coste) * parseLooseDecimal(updated.Un)) /
                100,
              2,
            )
          : safeDecimal(
              parseLooseDecimal(updated.Coste) * parseLooseDecimal(updated.Un),
              2,
            ));
      const venta = denominator !== 0 ? safeDecimal(conv / denominator, 2) : 0;

      updated.MargenVenta = margen;
      updated.Venta = venta;
      updated.CosteConvertido = conv;

      if (line.IdLineaPresupuestos) {
        await updateBudgetLine(line.IdLineaPresupuestos, {
          ESCANDALLO_GRUPOS: 13,
          ESCANDALLO_SUBGRUPO: subgrupoId,
          Un: updated.Un,
          Coste: updated.Coste,
          CosteConvertido: conv,
          MonedaCoste: "EUR",
          MonedaVenta: "EUR",
          TipoCambio: 1,
          MargenVenta: margen,
          Venta: venta,
        }).catch((e) => {
          console.warn("updateBudgetLine falló (no crítico):", e);
        });
      }

      return updated;
    };

    // === Actualizaciones ===

    // (A) LCL 203 → por UNIDAD (volPerUnit), coste de catálogo si lo tenemos
    const updated203 = await updateLine(
      203,
      volPerUnit,
      lclCostCat || undefined,
      null,
    );
    if (updated203)
      Object.assign(updated203, await calculateLine(updated203, true, false));

    // (B) INSURANCE 210 → % (0,2) sobre insuranceBase
    const updated210 = await updateLine(
      210,
      insuranceUn,
      safeDecimal(insuranceBase, 2),
      null,
    );
    if (updated210)
      Object.assign(updated210, await calculateLine(updated210, true, true));

    // (C) ARANCEL 192 → dutyPct % sobre base refrescada
    const updated192 = await updateLine(
      192,
      safeDecimal(effectiveDutyPct, 6),
      safeDecimal(refreshedSubtotalBaseImport, 2),
      null,
    );
    if (updated192)
      Object.assign(updated192, await calculateLine(updated192, true, true));

    // (D) CAUTION 209 → 0,5% sobre el IMPORTE de ARANCEL
    const dutyAmount = computeDutyAmount(
      refreshedSubtotalBaseImport,
      effectiveDutyPct,
      updated192?.CosteConvertido,
    );

    const updated209 = await updateLine(
      209,
      cautionUn,
      safeDecimal(dutyAmount, 2),
      null,
    );
    if (updated209)
      Object.assign(updated209, await calculateLine(updated209, true, true));

    // (E) HANDLING 204 y DELIVERY 198 → per CBM (volPerUnit), con coste catálogo si disponible
    for (const [id, catCost] of [
      [204, handlingCostCat],
      [198, deliveryCostCat],
    ]) {
      const upd = await updateLine(id, volPerUnit, catCost || undefined, null);
      if (upd) Object.assign(upd, await calculateLine(upd, true, false));
    }

    // 7) Refleja en estado local (sin machacar costes a 0)
    const updated = allLines.map((line) => {
      if (Number(line.ESCANDALLO_GRUPOS) !== 13) return line;
      const sg = Number(line.ESCANDALLO_SUBGRUPO);
      if (sg === 203 && updated203) return { ...line, ...updated203 };
      if (sg === 210 && updated210) return { ...line, ...updated210 };
      if (sg === 192 && updated192) return { ...line, ...updated192 };
      if (sg === 209 && updated209) return { ...line, ...updated209 };

      const CAT = {
        203: lclCostCat,
        204: handlingCostCat,
        198: deliveryCostCat,
      };

      if ((sg === 204 || sg === 198) && volPerUnit > 0) {
        const keepCost = parseLooseDecimal(line.Coste);
        const cat = CAT[sg] || 0;
        return { ...line, Un: volPerUnit, Coste: cat > 0 ? cat : keepCost };
      }

      return line;
    });

    setLines(updated);
  };

  const handleLineChange = async (index, field, value) => {
    // Coste / Units / márgenes / venta: solo draft mientras se teclea.
    // Recálculo + persistencia en commitLineField (blur) para no perder el foco.
    if (NUMERIC_DRAFT_FIELDS.includes(field)) {
      setLines((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], [field]: value };
        return next;
      });
      return;
    }

    const updatedLines = [...lines];
    updatedLines[index] = { ...updatedLines[index], [field]: value };
    const line = updatedLines[index];

    const subgruposInfo = line.subgruposList?.find(
      (s) =>
        s.ID_SUBGRUPO ===
        (field === "ESCANDALLO_SUBGRUPO" ? value : line.ESCANDALLO_SUBGRUPO),
    );

    const proveedor = tiposProveedores.find(
      (p) => p.CODPROV === line.PROVEEDOR_ESCANDALLO,
    );
    const pais = proveedor?.PAIS?.toLowerCase();
    const duty =
      pais === "china"
        ? parseFloat(subgruposInfo?.DutiesChina) || 0
        : pais === "india"
          ? parseFloat(subgruposInfo?.DutiesIndia) || 0
          : 0;

    const volPerUnit =
      parseFloat(presupuesto.VOL?.toString().replace(",", ".")) || 0;

    if (field === "ESCANDALLO_SUBGRUPO" && [203, 204, 198].includes(value)) {
      updatedLines[index].Un = volPerUnit;
    }

    if (field === "ESCANDALLO_SUBGRUPO" && value !== 192) {
      const arancelIndex = updatedLines.findIndex(
        (l) => l.ESCANDALLO_GRUPOS === 13 && l.ESCANDALLO_SUBGRUPO === 192,
      );
      if (arancelIndex !== -1) {
        updatedLines[arancelIndex] = {
          ...updatedLines[arancelIndex],
          Un: duty,
        };
      }
    }

    setLines([...updatedLines]);

    const seq = ++lineCalcSeqRef.current;
    const editedIndex = index;

    const updatedLine = await calculateLine(
      {
        ...updatedLines[index],
        MargenVenta: updatedLines[index].MargenVenta ?? 10,
      },
      false,
      false,
      field,
    );

    if (seq !== lineCalcSeqRef.current) return;

    setLines((prev) => {
      const next = [...prev];
      const current = next[editedIndex];
      if (!current) return prev;
      next[editedIndex] = {
        ...updatedLine,
        _rowKey: current._rowKey || updatedLine._rowKey,
      };
      debounceUpdateLine(next[editedIndex]);
      return next;
    });

    if (
      updatedLine.ESCANDALLO_GRUPOS !== 13 ||
      updatedLine.ESCANDALLO_SUBGRUPO === 203
    ) {
      setTimeout(() => {
        if (seq !== lineCalcSeqRef.current) return;
        recalculateImportAutoLines([...linesRef.current]);
      }, 150);
    }
  };

  /** Al salir del input numérico: normaliza, recalcula y guarda. */
  const commitLineField = async (index, field) => {
    if (!NUMERIC_DRAFT_FIELDS.includes(field)) return;
    const current = linesRef.current[index];
    if (!current || isLocked) return;

    const raw = current[field];
    let committed = raw;
    if (typeof raw === "string") {
      const trimmed = raw.trim().replace(",", ".");
      if (
        trimmed === "" ||
        trimmed === "-" ||
        trimmed === "." ||
        trimmed === "-."
      ) {
        committed = field === "MargenVenta" ? 10 : 0;
      } else {
        const parsed = Number(trimmed);
        committed = Number.isFinite(parsed) ? parsed : 0;
      }
    }

    const seq = ++lineCalcSeqRef.current;
    const draftLine = {
      ...current,
      [field]: committed,
      MargenVenta:
        field === "MargenVenta" ? committed : (current.MargenVenta ?? 10),
    };

    setLines((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = { ...next[index], [field]: committed };
      return next;
    });

    const updatedLine = await calculateLine(draftLine, false, false, field);
    if (seq !== lineCalcSeqRef.current) return;

    const merged = {
      ...updatedLine,
      _rowKey: current._rowKey || updatedLine._rowKey,
    };

    setLines((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = merged;
      return next;
    });
    debounceUpdateLine(merged);

    if (
      merged.ESCANDALLO_GRUPOS !== 13 ||
      merged.ESCANDALLO_SUBGRUPO === 203
    ) {
      setTimeout(() => {
        if (seq !== lineCalcSeqRef.current) return;
        recalculateImportAutoLines([...linesRef.current]);
      }, 150);
    }
  };

  const debounceUpdateLine = debounce(async (line) => {
    const n = (v) => {
      const t = typeof v === "string" ? String(v).replace(",", ".") : v;
      const x = Number(t);
      return Number.isFinite(x) ? x : 0;
    };

    const lineData = {
      ESCANDALLO_GRUPOS: line.ESCANDALLO_GRUPOS,
      ESCANDALLO_SUBGRUPO: line.ESCANDALLO_SUBGRUPO,
      Proveedor: line.PROVEEDOR_ESCANDALLO || null,
      Un: n(line.Un) || 0,
      Coste: n(line.Coste) || 0,
      Venta: n(line.Venta) || 0,
      MargenCoste: line.MargenCoste != null ? n(line.MargenCoste) : null,
      MargenVenta: line.MargenVenta != null ? n(line.MargenVenta) : null,
      MonedaCoste: line.MonedaCoste || "EUR",
      MonedaVenta: line.MonedaVenta || "EUR",
      TipoCambio: n(line.TipoCambio) || 1,
      CosteConvertido: n(line.CosteConvertido) || 0,
    };

    const canCreate =
      Number(line.ESCANDALLO_GRUPOS) > 0 &&
      Number(line.ESCANDALLO_SUBGRUPO) > 0 &&
      ([192, 209, 210].includes(Number(line.ESCANDALLO_SUBGRUPO))
        ? true
        : n(line.Un) > 0);

    try {
      if (line.IdLineaPresupuestos) {
        await updateBudgetLine(line.IdLineaPresupuestos, lineData);
        return;
      }
      if (!canCreate || !presupuestoId) return;

      const created = await createBudgetLine(presupuestoId, lineData);
      const newId = Number(created?.IdLineaPresupuestos);
      if (!Number.isFinite(newId) || newId <= 0) return;

      setLines((prev) =>
        prev.map((l) => {
          if (l.IdLineaPresupuestos) return l;
          // Empareja el borrador por grupo/subgrupo/proveedor/un (mejor esfuerzo)
          const same =
            Number(l.ESCANDALLO_GRUPOS) === Number(line.ESCANDALLO_GRUPOS) &&
            Number(l.ESCANDALLO_SUBGRUPO) === Number(line.ESCANDALLO_SUBGRUPO) &&
            String(l.PROVEEDOR_ESCANDALLO || "") ===
              String(line.PROVEEDOR_ESCANDALLO || "") &&
            n(l.Un) === n(line.Un) &&
            n(l.Coste) === n(line.Coste);
          // Conserva _rowKey para no remontar la fila (perdería el foco del input).
          return same ? { ...l, IdLineaPresupuestos: newId } : l;
        }),
      );
    } catch (error) {
      console.error("Error saving line on server:", error);
    }
  }, 500);

  const handleCurrencyChange = async (index, field, currency) => {
    if (isLocked) return;

    const updated = [...lines];
    updated[index] = { ...updated[index], [field]: currency };

    // Recalcula la línea con la nueva moneda/tipo cambio
    const computed = await calculateLine(updated[index]);
    updated[index] = computed;

    setLines(updated);

    // Si la línea NO es import (grupo 13) o es LCL (203), recalcula las automáticas
    if (
      computed.ESCANDALLO_GRUPOS !== 13 ||
      computed.ESCANDALLO_SUBGRUPO === 203
    ) {
      // usa el snapshot 'updated' para evitar carreras con setState
      recalculateImportAutoLines(updated);
    }
  };

  const handleGrupoChange = async (index, value) => {
    try {
      const subgrupos = await getEscandalloSubgruposMuestras(value);
      setLines((prevLines) =>
        prevLines.map((line, i) =>
          i === index
            ? {
                ...line,
                ESCANDALLO_GRUPOS: value,
                ESCANDALLO_SUBGRUPO: "",
                subgruposList: subgrupos || [],
              }
            : line,
        ),
      );
    } catch (error) {
      console.error("Error al cargar subgrupos:", error);
    }
  };

  // Carga inicial
  useEffect(() => {
    let isMounted = true;

    const cargarLineas = async () => {
      const updatedLines = await Promise.all(
        (initialLines || []).map(async (line) => {
          const subgruposList = line.ESCANDALLO_GRUPOS
            ? await getEscandalloSubgruposMuestras(line.ESCANDALLO_GRUPOS)
            : [];

          return withRowKey({
            ...line,
            Un:
              line.Un !== null && !isNaN(Number(line.Un))
                ? Number(parseFloat(line.Un).toFixed(6))
                : 0,
            subgruposList,
          });
        }),
      );

      if (isMounted) setLines(updatedLines);
    };

    const cargarDesdeBackend = async () => {
      try {
        const proveedores = await getProveedores();
        const sortedProveedores = [...proveedores].sort((a, b) =>
          a.N_PROVEEDOR.toLowerCase().localeCompare(
            b.N_PROVEEDOR.toLowerCase(),
          ),
        );
        setTiposProveedores(sortedProveedores);

        const loadedLines = await fetchBudgetLines(presupuestoId);

        if (Array.isArray(loadedLines)) {
          const updatedLines = await Promise.all(
            loadedLines.map(async (line) => {
              const subgruposList = line.ESCANDALLO_GRUPOS
                ? await getEscandalloSubgruposMuestras(line.ESCANDALLO_GRUPOS)
                : [];

              const matchingProveedor = sortedProveedores.find(
                (prov) =>
                  String(prov.N_PROVEEDOR).toLowerCase() ===
                    String(line.Proveedor).toLowerCase() ||
                  String(prov.CODPROV) === String(line.Proveedor),
              );

              return withRowKey({
                ...line,
                Un:
                  line.Un !== null && !isNaN(Number(line.Un))
                    ? Number(parseFloat(line.Un).toFixed(6))
                    : 0,
                subgruposList,
                PROVEEDOR_ESCANDALLO: matchingProveedor
                  ? matchingProveedor.CODPROV
                  : "",
              });
            }),
          );

          if (isMounted) setLines(updatedLines);
        } else {
          if (isMounted) setLines([]);
        }
      } catch (error) {
        console.error("Error al cargar desde backend:", error);
      }
    };

    if (presupuestoId) {
      cargarDesdeBackend();
    } else {
      cargarLineas();
    }

    return () => {
      isMounted = false;
    };
  }, [refreshKey, presupuestoId]);

  useEffect(() => {
    ddpRecalcDoneRef.current = false;
  }, [presupuestoId, refreshKey, presupuesto?.VOL, presupuesto?.Units]);

  // Recalcula líneas import DDP al cargar si faltan duties (p. ej. arancel Un=0)
  useEffect(() => {
    if (ddpRecalcDoneRef.current) return;
    if (Number(presupuesto?.Id_Incoterm) !== 4) return;
    if (!presupuestoId || lines.length === 0) return;
    if (tiposProveedores.length === 0) return;
    if (!materialSubgrupos?.length && !escandalloSubgrupos?.length) return;
    if (!ddpTarifas.length) return;
    if (!lines.some((l) => Number(l.ESCANDALLO_GRUPOS) === 13)) return;

    ddpRecalcDoneRef.current = true;
    recalculateImportAutoLines(lines);
  }, [
    presupuesto?.Id_Incoterm,
    presupuesto?.VOL,
    presupuesto?.Units,
    presupuestoId,
    refreshKey,
    lines,
    tiposProveedores.length,
    materialSubgrupos,
    escandalloSubgrupos,
    ddpTarifas,
  ]);

  // Limpia si no hay presupuestoId
  useEffect(() => {
    if (!presupuestoId) setLines([]);
  }, [presupuestoId]);

  return (
    <Box
      sx={{
        width: "100%",
        paddingLeft: { xs: "0.35em", md: "0.75em" },
        paddingRight: { xs: "0.35em", md: "0.75em" },
        textAlign: "initial",
        background: "transparent",
      }}
    >
      <Box
        sx={{
          display: "flex",
          marginTop: "0.1em",
          justifyContent: "flex-end",
          alignItems: "center",
          mb: 0.9,
          gap: 1,
          px: { xs: 0.25, md: 0.5 },
        }}
      >
        <IconButton
          onClick={handleAddLine}
          disabled={isLocked}
          sx={{
            color: "#34afc7",
            backgroundColor: alpha(theme.palette.common.white, 0.96),
            boxShadow: `0 10px 22px ${alpha(theme.palette.primary.main, 0.12)}`,
            "&.MuiIconButton-root": { border: "1px solid #34afc7" },
            "&.MuiButtonBase-root": {
              height: "2rem",
              width: "2rem",
              mt: 1,
            },
            "&:hover": { backgroundColor: "#34afc7", color: "#fff" },
          }}
        >
          <Add />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: { xs: "none", md: "grid" },
          width: "100%",
          gridTemplateColumns: lineGridTemplateColumns,
          gap: 0.7,
          alignItems: "center",
          px: 0.7,
          pb: 0.6,
        }}
      >
        {[
          "Group",
          "Subgroup",
          "Supplier",
          "Units",
          "C. Cost",
          "Cost",
          "Exch.",
          "Conv Cost",
          "C. Sale",
          "Margin %",
          "Margin",
          "Sale",
          "",
        ].map((label) => (
          <Typography
            key={label || "actions"}
            sx={{
              fontSize: "0.63rem",
              fontWeight: 800,
              letterSpacing: 0.35,
              textTransform: "uppercase",
              color: "text.secondary",
              px: label ? 0.35 : 0,
              minWidth: 0,
              textAlign:
                label === "Units" ||
                label === "Cost" ||
                label === "Exch." ||
                label === "Conv Cost" ||
                label === "Margin %" ||
                label === "Margin" ||
                label === "Sale"
                  ? "right"
                  : "left",
            }}
          >
            {label}
          </Typography>
        ))}
      </Box>

      <Stack spacing={isMobile ? 1.25 : 0}>
      {Array.isArray(lines) &&
        lines.map((line, index) => {
          const stableKey = line._rowKey || `fallback-${index}`;
          return (
            <Box
              key={stableKey}
              sx={{
                display: "grid",
                width: "100%",
                minWidth: { xs: "100%", md: 980 },
                gridTemplateColumns: {
                  xs: "1fr",
                  md: lineGridTemplateColumns,
                },
                gap: { xs: 0.85, md: 0.7 },
                alignItems: "center",
                "& > *": {
                  minWidth: 0,
                },
                ...(isMobile
                  ? {
                      ...pageMobileCardSx(theme, {
                        accentColor: theme.palette.primary.main,
                      }),
                      overflow: "visible",
                    }
                  : {
                      borderRadius: 3,
                      border: "1px solid",
                      borderColor: alpha(theme.palette.primary.main, 0.08),
                      backgroundColor: alpha(theme.palette.common.white, 0.94),
                      boxShadow: `0 12px 26px ${alpha(theme.palette.common.black, 0.05)}`,
                    }),
                px: { xs: 1.25, md: 0.7 },
                py: { xs: 1.25, md: 0.7 },
              }}
            >
              {labeledField(
                "Group",
                <FormControl
                sx={{ width: "100%", minWidth: 0, ...softFieldSx }}
                disabled={isLocked}
              >
                <Select
                  sx={{
                    textAlign: "left",
                    height: compactFieldHeight,
                    minHeight: compactFieldHeight,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  value={line.ESCANDALLO_GRUPOS || ""}
                  onChange={(e) =>
                    !isLocked && handleGrupoChange(index, e.target.value)
                  }
                >
                  {escandalloGrupos.map((grupo) => (
                    <MenuItem key={grupo.ID_GRUPO} value={grupo.ID_GRUPO}>
                      {grupo.GRUPO}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              )}

              {labeledField(
                "Subgroup",
                <FormControl sx={{ width: "100%", minWidth: 0, ...softFieldSx }}>
                <Select
                  sx={{
                    textAlign: "left",
                    height: compactFieldHeight,
                    minHeight: compactFieldHeight,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  value={line.ESCANDALLO_SUBGRUPO || ""}
                  onChange={(e) =>
                    !isLocked &&
                    handleLineChange(
                      index,
                      "ESCANDALLO_SUBGRUPO",
                      e.target.value,
                    )
                  }
                  disabled={isLocked || !line.ESCANDALLO_GRUPOS}
                >
                  {Array.isArray(line.subgruposList) &&
                    line.subgruposList.map((subgrupo) => (
                      <MenuItem
                        key={subgrupo.ID_SUBGRUPO}
                        value={subgrupo.ID_SUBGRUPO}
                      >
                        {subgrupo.SUBGRUPO}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
              )}

              {labeledField(
                "Supplier",
                <FormControl
                sx={{ width: "100%", minWidth: 0, ...softFieldSx }}
                disabled={isLocked}
              >
                <Select
                  sx={{
                    textAlign: "left",
                    height: compactFieldHeight,
                    minHeight: compactFieldHeight,
                    "& .MuiSelect-select": {
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    },
                  }}
                  value={line.PROVEEDOR_ESCANDALLO || ""}
                  onChange={(e) =>
                    !isLocked &&
                    handleLineChange(
                      index,
                      "PROVEEDOR_ESCANDALLO",
                      e.target.value,
                    )
                  }
                >
                  {tiposProveedores.length > 0 ? (
                    tiposProveedores.map((proveedor) => (
                      <MenuItem
                        key={proveedor.CODPROV}
                        value={proveedor.CODPROV}
                      >
                        {proveedor.N_PROVEEDOR} ({proveedor.PAIS})
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>Cargando proveedores...</MenuItem>
                  )}
                </Select>
              </FormControl>
              )}

              <FieldPair isMobile={isMobile}>
              {labeledField(
                "Units",
                <TextField
                type="text"
                value={
                  typeof line.Un === "number"
                    ? line.Un.toString()
                    : line.Un || ""
                }
                onChange={(e) => {
                  if (!isLocked) {
                    const normalized = normalizeDecimalTyping(e.target.value);
                    if (normalized !== null) {
                      handleLineChange(index, "Un", normalized);
                    }
                  }
                }}
                onBlur={() => commitLineField(index, "Un")}
                fullWidth
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input": {
                    color: "#34afc7",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                }}
                inputProps={{ inputMode: "decimal", step: "any" }}
                disabled={isLocked}
              />
              )}

              {labeledField(
                "C.Cost",
                <Button
                size="small"
                disabled={isLocked}
                onClick={(event) =>
                  openCurrencyMenu(event, index, "MonedaCoste")
                }
                endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  height: compactFieldHeight,
                  minHeight: compactFieldHeight,
                  px: 0.45,
                  py: 0.2,
                  borderRadius: 2.4,
                  justifyContent: "space-between",
                  color: "text.primary",
                  backgroundColor: alpha(theme.palette.common.white, 0.98),
                  border: "1px solid transparent",
                  boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.05)}`,
                  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                    alignSelf: "center",
                  },
                  "& .MuiButton-endIcon": { ml: 0.15, mr: -0.2 },
                }}
              >
                {renderCurrencyPill(line.MonedaCoste)}
              </Button>
              )}
              </FieldPair>

              <FieldPair isMobile={isMobile}>
              {labeledField(
                "Cost",
                <TextField
                type="text"
                value={line.Coste || ""}
                onChange={(e) => {
                  if (isLocked) return;
                  const normalized = normalizeDecimalTyping(e.target.value);
                  if (normalized !== null) {
                    handleLineChange(index, "Coste", normalized);
                  }
                }}
                onBlur={() => commitLineField(index, "Coste")}
                inputProps={{ inputMode: "decimal", step: "any" }}
                disabled={isLocked}
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input": {
                    color: "rgb(204 15 15 / 87%)",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                }}
              />
              )}

              {labeledField(
                "Exch.",
                <TextField
                type="text"
                value={line.TipoCambio || ""}
                disabled
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#9e9e9e",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                  "& .Mui-disabled": { WebkitTextFillColor: "#9e9e9e" },
                }}
              />
              )}
              </FieldPair>

              <FieldPair isMobile={isMobile}>
              {labeledField(
                "Conv Cost",
                <TextField
                type="text"
                value={
                  Number.isFinite(Number(line.CosteConvertido))
                    ? Number(line.CosteConvertido).toFixed(2)
                    : ""
                }
                disabled
                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "rgb(204 15 15 / 87%)",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                  "& .Mui-disabled": {
                    WebkitTextFillColor: "rgb(204 15 15 / 87%)",
                  },
                }}
              />
              )}

              {labeledField(
                "C.Sale",
                <Button
                size="small"
                disabled={isLocked}
                onClick={(event) =>
                  openCurrencyMenu(event, index, "MonedaVenta")
                }
                endIcon={<KeyboardArrowDownRoundedIcon sx={{ fontSize: 16 }} />}
                sx={{
                  minWidth: 0,
                  width: "100%",
                  height: compactFieldHeight,
                  minHeight: compactFieldHeight,
                  px: 0.45,
                  py: 0.2,
                  borderRadius: 2.4,
                  justifyContent: "space-between",
                  color: "text.primary",
                  backgroundColor: alpha(theme.palette.common.white, 0.98),
                  border: "1px solid transparent",
                  boxShadow: `0 10px 22px ${alpha(theme.palette.common.black, 0.05)}`,
                  "& .MuiButton-startIcon, & .MuiButton-endIcon": {
                    alignSelf: "center",
                  },
                  "& .MuiButton-endIcon": { ml: 0.15, mr: -0.2 },
                }}
              >
                {renderCurrencyPill(line.MonedaVenta)}
              </Button>
              )}
              </FieldPair>

              <FieldPair isMobile={isMobile}>
              {labeledField(
                "Margin %",
                <TextField
                type="text"
                value={line.MargenVenta || ""}
                onChange={(e) => {
                  if (isLocked) return;
                  const normalized = normalizeDecimalTyping(e.target.value);
                  if (normalized !== null) {
                    handleLineChange(index, "MargenVenta", normalized);
                  }
                }}
                onBlur={() => commitLineField(index, "MargenVenta")}
                inputProps={{ inputMode: "decimal", step: "any" }}
                disabled={isLocked || !isLineComplete(line)}
                InputProps={{
                  endAdornment: (
                    <span
                      style={{
                        marginLeft: 6,
                        color: "#757575",
                        fontWeight: 500,
                        fontSize: ".8rem",
                      }}
                    >
                      %
                    </span>
                  ),
                }}
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input": {
                    color: "#00c2a3 !important",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                }}
              />
              )}

              {labeledField(
                "Margin",
                <TextField
                type="text"
                value={
                  line.MargenCoste !== undefined && line.MargenCoste !== null
                    ? line.MargenCoste
                    : line.Venta !== undefined &&
                        line.CosteConvertido !== undefined
                      ? (line.Venta - line.CosteConvertido).toFixed(2)
                      : ""
                }
                onChange={(e) => {
                  const isImportLine = Number(line.ESCANDALLO_GRUPOS) === 13;
                  if (isLocked || isImportLine) return;
                  const normalized = normalizeDecimalTyping(e.target.value);
                  if (normalized !== null) {
                    handleLineChange(index, "MargenCoste", normalized);
                  }
                }}
                onBlur={() => commitLineField(index, "MargenCoste")}
                inputProps={{ inputMode: "decimal", step: "any" }}
                disabled={
                  isLocked ||
                  Number(line.ESCANDALLO_GRUPOS) === 13 ||
                  !isLineComplete(line)
                }
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input": {
                    color: "#00c2a3",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".8rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#00c2a3",
                    WebkitTextFillColor: "#00c2a3",
                  },
                }}
              />
              )}
              </FieldPair>

              <FieldPair isMobile={isMobile}>
              {labeledField(
                "Sale",
                <TextField
                type="text"
                value={
                  line.Venta !== undefined && line.Venta !== null
                    ? line.Venta
                    : ""
                }
                onChange={(e) => {
                  const isImportLine = Number(line.ESCANDALLO_GRUPOS) === 13;
                  if (isLocked || isImportLine) return;
                  const normalized = normalizeDecimalTyping(e.target.value);
                  if (normalized !== null) {
                    handleLineChange(index, "Venta", normalized);
                  }
                }}
                onBlur={() => commitLineField(index, "Venta")}
                inputProps={{ inputMode: "decimal", step: "any" }}
                disabled={
                  isLocked ||
                  Number(line.ESCANDALLO_GRUPOS) === 13 ||
                  !isLineComplete(line)
                }
                sx={{
                  ...softFieldSx,
                  "& .MuiInputBase-input": {
                    color: "#00c2a3",
                    fontWeight: 800,
                    textAlign: "right",
                    fontSize: ".9rem",
                  },
                  "& .MuiInputBase-input.Mui-disabled": {
                    color: "#00c2a3",
                    WebkitTextFillColor: "#00c2a3",
                  },
                }}
              />
              )}

              {labeledField(
                "",
                <IconButton
                onClick={() => !isLocked && handleRemoveLine(index)}
                disabled={isLocked}
                sx={{
                  justifySelf: "end",
                  backgroundColor: alpha(theme.palette.error.main, 0.08),
                  boxShadow: `0 10px 22px ${alpha(theme.palette.error.main, 0.12)}`,
                  color: "error.main",
                  "&.MuiIconButton-root": {
                    border: "1px solid rgba(211, 47, 47, 0.28)",
                  },
                  "&.MuiButtonBase-root": {
                    height: "2rem",
                    width: "2rem",
                  },
                  "&:hover": {
                    backgroundColor: "error.main",
                    color: "#fff",
                  },
                }}
              >
                <DeleteOutlinedIcon />
              </IconButton>
              )}
              </FieldPair>

              {isMobile ? (
                <Typography
                  sx={{
                    position: "absolute",
                    top: 10,
                    right: 12,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    color: alpha(theme.palette.text.secondary, 0.85),
                  }}
                >
                  #{index + 1}
                </Typography>
              ) : null}
            </Box>
          );
        })}
      </Stack>

      <Dialog
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
      >
        <DialogTitle>¿Estás seguro?</DialogTitle>
        <DialogContent>
          ¿Deseas eliminar esta línea de presupuesto?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)}>Cancelar</Button>
          <Button onClick={confirmRemoveLine} color="error">
            Eliminar
          </Button>
        </DialogActions>
      </Dialog>
      <Menu
        anchorEl={currencyMenu.anchorEl}
        open={Boolean(currencyMenu.anchorEl)}
        onClose={closeCurrencyMenu}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            mt: 0.5,
            borderRadius: 2.5,
            border: "1px solid",
            borderColor: alpha(theme.palette.divider, 0.9),
            boxShadow: `0 18px 40px ${alpha(theme.palette.common.black, 0.12)}`,
          },
        }}
      >
        {currencyList.map((currency) => (
          <MenuItem
            key={currency}
            onClick={async () => {
              if (currencyMenu.index !== null && currencyMenu.field) {
                await handleCurrencyChange(
                  currencyMenu.index,
                  currencyMenu.field,
                  currency,
                );
              }
              closeCurrencyMenu();
            }}
            sx={{ gap: 0.8, minHeight: 34 }}
          >
            {renderCurrencyPill(currency)}
            <Box component="span">{currency}</Box>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

export default EstimatesLineas;
